import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Alert, Typography, MenuItem, TextField,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { superAdminAPI } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { formatMoney } from '../../utils/localeFormat';
import {
  SuperAdminDataTable, apiErrorMessage, statusChip,
} from '../../components/superadmin/SuperAdminDataTable';
import { SuperAdminDetailDrawer, SuperAdminDetailField } from '../../components/superadmin/SuperAdminDetailDrawer';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';

const TRANSACTION_STATUS_OPTIONS = [
  { label: 'Paid', value: 'paid' },
  { label: 'Pending', value: 'pending' },
  { label: 'Failed', value: 'failed' },
  { label: 'Refunded', value: 'refunded' },
];

const EXTRA_EXPORT_COLUMNS = [
  { id: 'tenantEmail', header: 'Client Email', exportValue: (r: any) => r.tenantEmail, searchValue: (r: any) => r.tenantEmail },
  { id: 'tenantMobile', header: 'Mobile', exportValue: (r: any) => r.tenantMobile, searchValue: (r: any) => r.tenantMobile },
  { id: 'tenantId', header: 'Tenant ID', exportValue: (r: any) => r.tenantId, searchValue: (r: any) => r.tenantId },
  { id: 'tenantGst', header: 'GSTIN', exportValue: (r: any) => r.tenantGst, searchValue: (r: any) => r.tenantGst },
  { id: 'tenantCity', header: 'City', exportValue: (r: any) => r.tenantCity },
  { id: 'tenantState', header: 'State', exportValue: (r: any) => r.tenantState },
  { id: 'currency', header: 'Currency', exportValue: (r: any) => r.currency || 'INR' },
  { id: 'createdAt', header: 'Recorded', exportValue: (r: any) => r.createdAt },
];

function buildTransactionFields(row: any, formatDate: (d: Date | string) => string, formatRowMoney: (r: any) => string): SuperAdminDetailField[] {
  const location = [row.tenantCity, row.tenantState, row.tenantPincode, row.tenantCountry].filter(Boolean).join(', ');
  return [
    { label: 'Reference', value: row.reference || `TXN-${String(row._id).slice(-6)}` },
    { label: 'Client', value: row.companyName || row.tenant },
    { label: 'Client Email', value: row.tenantEmail },
    { label: 'Mobile', value: row.tenantMobile },
    { label: 'Tenant ID', value: row.tenantId },
    { label: 'GSTIN', value: row.tenantGst },
    { label: 'Location', value: location, fullWidth: true },
    { label: 'Plan', value: row.plan || row.planName },
    { label: 'Amount', value: formatRowMoney(row) },
    { label: 'Currency', value: row.currency || 'INR' },
    { label: 'Payment Date', value: row.paidAt || row.date ? formatDate(row.paidAt || row.date) : '—' },
    { label: 'Recorded On', value: row.createdAt ? formatDate(row.createdAt) : '—' },
    { label: 'Transaction ID', value: String(row._id || ''), fullWidth: true },
  ];
}

export const TransactionsPage: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [detailRow, setDetailRow] = useState<any>(null);
  const navigate = useNavigate();
  const { formatDate } = useLocale();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {};
      if (tenantFilter !== 'all') params.tenantId = tenantFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;

      const txnRes = await superAdminAPI.getTransactions(params);
      setRows(txnRes.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load transactions'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    superAdminAPI.getTenants().then((r) => setTenants(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [tenantFilter, statusFilter, fromDate, toDate]);

  const tenantOptions = useMemo(
    () => tenants.map((t) => ({ id: t.tenantId, name: t.companyName })),
    [tenants]
  );

  const formatRowMoney = (row: any) =>
    formatMoney(row.amount, row.currency || 'INR', 'en');

  const openDetail = (row: any) => setDetailRow(row);

  const openFullReceipt = () => {
    if (!detailRow) return;
    navigate(`/super-admin/transactions/${detailRow._id}`, { state: { transaction: detailRow } });
  };

  return (
    <Box>
      <PageHeader
        title="Transactions"
        subtitle={getPageSubtitle('transactions')}
        icon={<ReceiptLongIcon />}

      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <TextField size="small" type="date" label="From Date" value={fromDate}
          onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }}
          sx={{ ...catchyTextFieldSx, width: 160 }} />
        <TextField size="small" type="date" label="To Date" value={toDate}
          onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }}
          sx={{ ...catchyTextFieldSx, width: 160 }} />
        <TextField select size="small" label="Tenant" value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          sx={{ minWidth: 220, ...catchyTextFieldSx }}>
          <MenuItem value="all">All Tenants</MenuItem>
          {tenantOptions.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Status" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 160, ...catchyTextFieldSx }}>
          <MenuItem value="all">All Status</MenuItem>
          {TRANSACTION_STATUS_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
      </Box>

      <SuperAdminDataTable
        rows={rows}
        loading={loading}
        searchPlaceholder="Search reference, client, email, GST, plan..."
        getRowId={(r) => String(r._id)}
        exportFileName="super-admin-transactions"
        exportSheetName="Transactions"
        defaultRowsPerPage={25}
        exportExtraColumns={EXTRA_EXPORT_COLUMNS}
        onRowClick={openDetail}
        columns={[
          {
            id: 'reference',
            header: 'Reference',
            searchValue: (r) => `${r.reference || ''} ${r._id || ''}`,
            exportValue: (r) => r.reference || r._id,
            render: (r) => (
              <Typography fontWeight={600} fontSize={13} fontFamily="monospace">
                {r.reference || `TXN-${String(r._id).slice(-6)}`}
              </Typography>
            ),
          },
          {
            id: 'tenant',
            header: 'Client',
            searchValue: (r) => `${r.companyName || r.tenant} ${r.tenantEmail || ''} ${r.tenantMobile || ''}`,
            exportValue: (r) => r.companyName || r.tenant,
            render: (r) => (
              <Typography fontWeight={600} fontSize={13}>{r.companyName || r.tenant}</Typography>
            ),
          },
          {
            id: 'plan',
            header: 'Plan',
            searchValue: (r) => r.plan || r.planName,
            exportValue: (r) => r.plan || r.planName,
            render: (r) => r.plan || r.planName,
          },
          {
            id: 'amount',
            header: 'Amount',
            searchValue: (r) => String(r.amount),
            exportValue: (r) => r.amount,
            sortValue: (r) => r.amount,
            sortable: true,
            render: (r) => formatRowMoney(r),
          },
          {
            id: 'status',
            header: 'Status',
            exportValue: (r) => r.status,
            render: (r) => statusChip(r.status),
          },
          {
            id: 'paidAt',
            header: 'Payment Date',
            searchValue: (r) => String(r.paidAt || r.date || ''),
            exportValue: (r) => r.paidAt || r.date,
            sortValue: (r) => new Date(r.paidAt || r.date || 0).getTime(),
            sortable: true,
            render: (r) => formatDate(r.paidAt || r.date),
          },
        ]}
      />

      <SuperAdminDetailDrawer
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        moduleTitle="Transaction"
        title={detailRow?.reference || (detailRow ? `TXN-${String(detailRow._id).slice(-6)}` : '')}
        subtitle={detailRow?.companyName || detailRow?.tenant}
        status={detailRow?.status}
        fields={detailRow ? buildTransactionFields(detailRow, formatDate, formatRowMoney) : []}
        primaryActions={detailRow ? (
          <>
            <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={openFullReceipt} sx={catchyPrimaryButtonSx}>
              Receipt & PDF
            </Button>
            <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={openFullReceipt}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Open Full Page
            </Button>
          </>
        ) : undefined}
      />
    </Box>
  );
};
